export const METRIC_UNITS = new Set(["g", "kg", "ml", "l"]);

export const UNIT_WORDS = [
  "cup",
  "tbsp",
  "tsp",
  "lb",
  "oz",
  "kg",
  "g",
  "ml",
  "l",
  "can",
  "head",
  "sheet",
  "dash",
];

// ── grammatical agreement ───────────────────────────────────────────────────
//
// Quantity/unit/noun agreement is decided from data on the ingredient, not
// from its spelling. `countability` ('count' | 'mass') and `subunit` come
// from pantry.ingredients / pantry.pantry_items and ride along in every
// payload; see back-end/add_content/countability.py.
//
// This used to be a hardcoded mass-noun list in this file, and it failed
// three times in a row — "buttermilks"/"spaghettis"/"baking powders", then
// "broccolis"/"escaroles", then "3/4 onions". Each round added the reported
// words to the list, and the class came back in the words nobody had
// reported yet. A guess that needs an exception list is a data field
// wearing a disguise, so it is now a field. Do not reintroduce a name
// vocabulary here: a wrong row is one UPDATE, a wrong list is a deploy.
//
// A payload with no `countability` is UNKNOWN, not mass: nothing is
// pluralized (the fail-safe direction — a missing "s" reads as a wording
// choice, a wrong one reads as a bug) but the quantity is still shown,
// because silently dropping "3" from "3 onion" loses information a shopper
// needs. Only an explicit 'mass' earns the name-alone rendering.
export const COUNT = "count";
export const MASS = "mass";
export const UNKNOWN = "unknown";

export function countabilityOf(ing) {
  const c = ing && ing.countability;
  return c === COUNT || c === MASS ? c : UNKNOWN;
}

// Words that disqualify a name from sub-unit formatting even when the row
// carries one — "3 cloves of garlic" is right, "3 cloves of garlic powder"
// is not. This is about the *modifier*, not the noun: a modified name is a
// different product from the one the sub-unit describes.
export const SUBUNIT_MODIFIER_RE = /\b(powder|paste|minced|roasted|granulated|flakes|salt|oil)\b/i;

// Units that never take a plural: symbols and standard abbreviations. Every
// other unit is a word ("cup", "clove", "head") and pluralizes normally.
export const INVARIANT_UNITS = new Set(["oz", "tbsp", "tsp", "lb", "g", "kg", "ml", "l"]);

// Named pieces you buy and use whole: a quantity in one of these always rounds
// up (see roundQty and countLineQuantity — both must agree, which is why the
// set is declared once here rather than inside either of them).
export const DISCRETE_UNITS = new Set([
  "head",
  "can",
  "sheet",
  "dash",
  "pinch",
  "leaf",
  "leaves",
  "clove",
  "cloves",
  "sprig",
  "sprigs",
  "slice",
  "slices",
  "piece",
  "pieces",
  "stalk",
  "stalks",
]);

// Units whose plural isn't a bare +s.
const IRREGULAR_UNIT_PLURALS = { leaf: "leaves" };

/**
 * Pluralize a unit word against a quantity: "1 cup" / "3 cups", "1 clove" /
 * "3 cloves", "1/2 cup", but "3 tbsp" and "8 oz" unchanged.
 *
 * Takes the quantity as a number or a display string — "3/4" and "1 1/2"
 * included. It must not use parseFloat: parseFloat("3/4") is 3, which is
 * how "3/4 onions" reached production.
 */
export function pluralizeUnit(unit, qty) {
  const u = (unit || "").trim();
  if (!u) return "";
  const lower = u.toLowerCase();
  if (INVARIANT_UNITS.has(lower)) return u;
  const n = parseFrac(qty);
  // Singular at or below one, plural above: "1/2 cup", "1 cup", "1 1/2 cups".
  // Not "!= 1" — that reads a fraction as plural and gives "3/4 cups", which
  // is the same agreement bug in a different word.
  if (isNaN(n) || n <= 1) return u;
  if (lower.endsWith("s")) return u;
  return IRREGULAR_UNIT_PLURALS[lower] ?? `${u}s`;
}

// Count-based ingredients where an arbitrary cooking fraction (thirds,
// eighths, ...) is genuinely nonsensical, not just unusual — you can't
// cleanly measure "2/3 of an egg" the way you can cut a quarter head of
// cabbage or a quarter lemon wedge with a knife. Mirrors the backend's
// add_content/normalize_batch.py _INDIVISIBLE_COUNT_INGREDIENTS. Scaling a
// recipe's servings (see scaleIng below) can produce quantities like
// 0.6667 for an egg; roundQty's default fraction table would faithfully
// display that as "2/3 eggs" — a real defect found in production — unless
// the ingredient is in this set, in which case only whole/half is offered.
export const INDIVISIBLE_COUNT_INGREDIENTS = new Set(["egg", "egg yolk"]);

// Snap a fractional part (0–1) to the nearest cooking fraction.
// Candidates: 0, 1/8, 1/4, 1/3, 1/2, 2/3, 3/4, 1
export const COOK_FRACS = [
  [0, ""],
  [1 / 8, "1/8"],
  [1 / 4, "1/4"],
  [1 / 3, "1/3"],
  [1 / 2, "1/2"],
  [2 / 3, "2/3"],
  [3 / 4, "3/4"],
  [1, ""],
];

export const PIECES_PER_LB = {
  "chicken breast": 2,
  "chicken thigh": 3,
  salmon: 2,
  "salmon fillet": 2,
  "catfish fillet": 2,
  "pork chop": 2,
  "sirloin tip steak": 1,
  steak: 1,
  potato: 2,
  "sweet potato": 2,
  apple: 3,
  pear: 3,
  eggplant: 1,
  zucchini: 2,
  "pizza dough": 1,
};

export const PIECES_LABEL = { "pizza dough": "ball" };

export function parseFrac(val) {
  if (typeof val !== "string") return Number(val);
  const parts = val.trim().split(/\s+/);
  if (parts.length === 2 && parts[1].includes("/")) {
    const [num, den] = parts[1].split("/").map(Number);
    return Number(parts[0]) + num / den;
  }
  if (parts.length === 1 && parts[0].includes("/")) {
    const [num, den] = parts[0].split("/").map(Number);
    return num / den;
  }
  return Number(val);
}

function gcd(a, b) {
  return b === 0 ? a : gcd(b, a % b);
}

// Snap a quantity to the nearest cooking fraction (see COOK_FRACS) and
// return it as an exact integer count of 1/24ths — the LCD of eighths and
// thirds, the two families COOK_FRACS mixes. Representing the snapped
// value this way (instead of as a float) means several already-snapped
// quantities can be summed with plain integer addition and no drift.
export function quantizeTo24(n) {
  if (isNaN(n) || n <= 0) return 0;
  const whole = Math.floor(n);
  const frac = n - whole;
  let best = COOK_FRACS[0];
  for (const c of COOK_FRACS) {
    if (Math.abs(c[0] - frac) < Math.abs(best[0] - frac)) best = c;
  }
  return Math.round((whole + best[0]) * 24);
}

// Render an exact count of 1/24ths as a reduced fraction string. Unlike
// quantizeTo24, this does NOT re-snap to the small COOK_FRACS menu — it's
// used to render sums of already-quantized quantities, which can land on
// fractions outside that menu (e.g. 1/3 + 1/2 = 5/6) but must still render
// exactly so a total reconciles with the per-recipe amounts that produced
// it, instead of being independently re-rounded to a different fraction.
export function formatFraction24(total24) {
  if (!total24 || total24 <= 0) return "";
  const whole = Math.floor(total24 / 24);
  const rem = total24 % 24;
  if (rem === 0) return String(whole);
  const g = gcd(rem, 24);
  const num = rem / g;
  const den = 24 / g;
  return whole > 0 ? `${whole} ${num}/${den}` : `${num}/${den}`;
}

export function toFractionString(n) {
  return formatFraction24(quantizeTo24(n));
}

// How far a snapped display may sit from the true amount before a unit
// conversion is considered lossy. COOK_FRACS has no eighths above 1/8, so a
// quantity landing on 7/8 snaps to 3/4 — a quarter of a pound of drift on a
// weight, silently.
const SNAP_TOLERANCE = 1e-6;

/**
 * Whether `n` survives the cooking-fraction display rounding unchanged.
 *
 * Used to gate unit *conversions*, which are optional: nothing forces 14 oz to
 * be shown in pounds, and 14 oz shown as "3/4 lb" is 12 oz — every standard can
 * in the catalog understated by two ounces, on the card and again in the
 * grocery total that summed the cards. A conversion that cannot be displayed
 * exactly is simply not made, and the original unit stands.
 *
 * Deliberately not a fix to COOK_FRACS: adding eighths would render 14 oz as
 * "7/8 lb", which is arithmetically right and not how anybody buys spinach.
 * It also means the gate widens on its own if that menu ever does grow.
 */
export function snapsExactly(n) {
  const snapped = parseFrac(toFractionString(n));
  return !isNaN(snapped) && Math.abs(snapped - n) < SNAP_TOLERANCE;
}

// Round a display quantity to a human-friendly value.
// Metric (g/ml/kg/l): integer. US volume (cup/tbsp/tsp): cooking fractions.
// Everything else: preserve as-is.
// `name` (optional) lets count-type (no-unit) quantities restrict which
// fractions are offered — see INDIVISIBLE_COUNT_INGREDIENTS.
export function roundQty(qty, unit, name) {
  const n = parseFrac(qty);
  if (isNaN(n) || n === 0) return qty;
  const u = (unit || "").toLowerCase();
  if (u === "g" || u === "ml") {
    if (n >= 50) return String(Math.round(n / 5) * 5);
    return String(Math.round(n));
  }
  if (u === "kg" || u === "l") {
    return String(Math.round(n * 10) / 10);
  }
  if (u === "cup" || u === "tbsp" || u === "tsp" || u === "lb" || u === "oz") {
    return toFractionString(n);
  }
  // no unit = count: ceil to nearest cooking fraction so user is never short
  if (!u) {
    const isIndivisible = INDIVISIBLE_COUNT_INGREDIENTS.has((name || "").toLowerCase().trim());
    const FRAC_VALS = isIndivisible
      ? [0, 1 / 2, 1]
      : [0, 1 / 8, 1 / 4, 1 / 3, 1 / 2, 2 / 3, 3 / 4, 1];
    const FRAC_LBLS = isIndivisible
      ? ["", "1/2", ""]
      : ["", "1/8", "1/4", "1/3", "1/2", "2/3", "3/4", ""];
    const whole = Math.floor(n);
    const frac = n - whole;
    const idx = FRAC_VALS.findIndex((f) => f >= frac - 0.001);
    const ceiled = FRAC_VALS[idx];
    if (ceiled === 1) return String(whole + 1);
    const label = FRAC_LBLS[idx];
    if (whole === 0) return label || (isIndivisible ? "1/2" : "1/8");
    return label ? `${whole} ${label}` : String(whole);
  }
  // discrete named units: round up — can't buy or use a fraction of these
  if (DISCRETE_UNITS.has(u)) {
    return String(Math.max(1, Math.ceil(n)));
  }
  // Any unit this function has never heard of. It used to return the quantity
  // untouched, which is the one branch that can put a raw float on screen —
  // "spinach 24.1071 oz". A stored unit outside the known set is a data
  // problem, but the display is not the place to prove it: snap it like every
  // other quantity so a user never reads a float, and let the audits find the
  // unit.
  return toFractionString(n);
}

/**
 * The quantity one line actually *displays* for a count-type row, as a number.
 *
 * Every count-type display rounds UP — you cannot buy two thirds of a clove —
 * so the displayed amount is almost always larger than the stored one. That
 * makes summing raw quantities and rounding the total at the end a different
 * number from adding up the visible lines: the parts each round up once, the
 * total rounds up once for all of them. Garlic showed as 3 + 3 + 3 cloves on
 * the recipe cards and 8 in the consolidated list, and the shopper who checked
 * came home a clove short.
 *
 * So aggregation sums *this* — what each card shows — rather than the exact
 * decimals behind them. The three cases here are exactly the three roundings
 * fmtIng/roundQty apply to a count row, and they must stay in step with them.
 */
export function countLineQuantity(ing) {
  const name = ing.ingredient_name || "";
  const n = parseFrac(ing.rawQuantity ?? ing.quantity);
  if (isNaN(n) || n <= 0) return 0;
  const unit = (ing.unit || "").trim().toLowerCase();
  // A named piece unit ("2 cloves", "3 leaves") and a sub-unit row ("3 cloves
  // of garlic") both display whole pieces.
  if (DISCRETE_UNITS.has(unit) || (!unit && ing.subunit && !SUBUNIT_MODIFIER_RE.test(name))) {
    return Math.max(1, Math.ceil(n));
  }
  // Bare count: ceiled to the nearest cooking fraction by roundQty.
  if (!unit) return parseFrac(roundQty(n, "", name));
  return n;
}

/**
 * English plural of a noun. Morphology only — whether a word *should* be
 * pluralized at all is countability, decided by the caller from data.
 */
export function pluralForm(name) {
  const base = (name || "").toLowerCase().trim();
  if (!base) return name;
  // already ends in s — treat as already plural ("pork chops", "pine nuts")
  if (base.endsWith("s")) return name;
  if (base.endsWith("ch") || base.endsWith("sh") || base.endsWith("o")) return name + "es";
  if (base.endsWith("y") && !/[aeiou]y$/.test(base)) return name.slice(0, -1) + "ies";
  // irregular f/fe → ves (leaf, half, loaf, shelf)
  if (/[aeo]f$/.test(base)) return name.slice(0, -1) + "ves";
  if (base.endsWith("fe")) return name.slice(0, -2) + "ves";
  return name + "s";
}

/**
 * Agreement for the ingredient noun itself.
 *
 * - A mass noun never pluralizes, at any quantity, with or without a unit:
 *   "3/4 lb broccoli", "2 oz escarole", "3 cups chicken broth".
 * - A count noun measured by a unit always pluralizes, because the plural
 *   agrees with the measured stuff and not with the number of units:
 *   "1 lb onions", "2 oz mushrooms".
 * - A count noun with no unit agrees with the quantity itself, and the
 *   quantity may be a fraction: "3/4 onion", "1 onion", "3 onions".
 *
 * `qty` may be a number or a display string ("3/4", "1 1/2"). It is read
 * with parseFrac, never parseFloat — parseFloat("3/4") is 3, which is
 * exactly how "3/4 onions" reached production.
 */
export function pluralizeName(name, qty, { countability, hasUnit = false } = {}) {
  if (countability !== COUNT) return name;
  if (hasUnit) return pluralForm(name);
  const n = parseFrac(qty);
  if (isNaN(n) || n <= 1) return name;
  return pluralForm(name);
}

export function metricUnitLabel(abbrev, qty) {
  const n = parseFrac(qty);
  const plural = !isNaN(n) && n > 1;
  switch (abbrev) {
    case "g":
      return plural ? "grams" : "gram";
    case "kg":
      return "kg";
    case "ml":
      return "ml";
    case "l":
      return plural ? "litres" : "litre";
    default:
      return abbrev;
  }
}

export function pieceHint(name, qty, unit) {
  const key = (name || "").toLowerCase().trim();
  const ppl = PIECES_PER_LB[key];
  if (!ppl) return "";
  // parseFrac, not parseFloat: `qty` is the already-formatted display string,
  // so it can be "1 1/2", which parseFloat reads as 1.
  const n = parseFrac(qty);
  if (!n || isNaN(n)) return "";
  const u = (unit || "").toLowerCase();
  let lbs;
  if (u === "lb") lbs = n;
  else if (u === "oz") lbs = n / 16;
  else if (u === "g") lbs = n / 450;
  else if (u === "kg") lbs = (n * 1000) / 450;
  else return "";
  const raw = lbs * ppl;
  if (raw < 0.75) return ""; // not enough to warrant showing a piece count
  const count = Math.round(raw);
  if (count < 1) return "";
  const label = PIECES_LABEL[key] ?? "piece";
  const noun = count === 1 ? label : label + "s";
  return `(about ${count} ${noun})`;
}

export function fmtIng(ing, metric = false) {
  const name = ing.ingredient_name || "";
  let metricUnit = (ing.unit_metric || "").toLowerCase();
  const isRealMetricConversion = metric && METRIC_UNITS.has(metricUnit) && ing.quantity_metric;

  let rawQ = isRealMetricConversion ? ing.quantity_metric : ing.rawQuantity ?? (ing.quantity || "");
  // Upgrade g→kg and ml→l when quantity >= 1000
  if (
    isRealMetricConversion &&
    (metricUnit === "g" || metricUnit === "ml") &&
    Number(rawQ) >= 1000
  ) {
    rawQ = Number(rawQ) / 1000;
    metricUnit = metricUnit === "g" ? "kg" : "l";
  }
  // Downgrade sub-¼-cup amounts to tbsp/tsp — e.g. 0.125 cup → 2 tbsp
  let displayUnit = ing.unit;
  if (!isRealMetricConversion) {
    const unitNorm = (ing.unit || "").toLowerCase().replace(/s$/, "");
    if (unitNorm === "cup") {
      const cupN = Number(rawQ);
      if (cupN > 0 && cupN < 0.25) {
        const tbspN = cupN * 16;
        if (tbspN < 1) {
          rawQ = String(cupN * 48);
          displayUnit = "tsp";
        } else {
          rawQ = String(tbspN);
          displayUnit = "tbsp";
        }
      }
    }
    // Upgrade oz → lb when ≥ 8 oz (½ lb and above) — but only when the pound
    // figure survives the fraction rounding intact. 14 oz is 7/8 lb, snaps to
    // 3/4, and shipped as "3/4 lb": two ounces short on every standard can in
    // the catalog. The ounce figure is exact and is what the tin says.
    if ((ing.unit || "").toLowerCase() === "oz") {
      const ozN = Number(rawQ);
      if (!isNaN(ozN) && ozN >= 8 && snapsExactly(ozN / 16)) {
        rawQ = String(ozN / 16);
        displayUnit = "lb";
      }
    }
    // ml → tbsp/tsp in US mode
    if ((ing.unit || "").toLowerCase() === "ml") {
      const mlN = parseFrac(rawQ);
      if (!isNaN(mlN) && mlN > 0) {
        if (mlN < 15) {
          rawQ = String(mlN / 5);
          displayUnit = "tsp";
        } else {
          rawQ = String(mlN / 15);
          displayUnit = "tbsp";
        }
      }
    }
    // g → oz in US mode
    if ((ing.unit || "").toLowerCase() === "g") {
      const gN = parseFrac(rawQ);
      if (!isNaN(gN) && gN > 0) {
        rawQ = String(gN / 28.35);
        displayUnit = "oz";
      }
    }
  }
  // A unit word that's just the ingredient name's own first word (e.g. unit
  // "leaf" + name "leaf lettuce") would otherwise render as a duplicated
  // "1 leaf leaf lettuce" — drop the redundant unit and let the name alone
  // carry it, since the ingredient's canonical name already says "leaf".
  const nameFirstWord = name.toLowerCase().trim().split(/\s+/)[0] || "";
  const unitCollidesWithName =
    !isRealMetricConversion &&
    displayUnit &&
    (displayUnit.toLowerCase().replace(/s$/, "") === nameFirstWord.replace(/s$/, "") ||
      displayUnit.toLowerCase() === nameFirstWord);
  // The unit is pluralized against the *displayed* quantity, so "1 cup" and
  // "3 cups" and "3/4 cup" all agree. Abbreviations never take a plural.
  const unitQty =
    !isRealMetricConversion && ing.frac24 != null && displayUnit === ing.unit
      ? ing.frac24 / 24
      : rawQ;
  const unit = unitCollidesWithName
    ? ""
    : isRealMetricConversion
    ? metricUnitLabel(metricUnit, rawQ)
    : displayUnit && displayUnit !== "unit"
    ? pluralizeUnit(displayUnit, unitQty)
    : "";
  // Aggregated grocery totals carry an exact 1/24ths sum (see
  // ingredientCategories.aggregate) built from the same per-recipe
  // quantization roundQty would apply individually. Render that sum
  // directly instead of re-rounding the summed decimal — re-rounding an
  // exact sum like 1/3 + 1/2 = 5/6 to the nearest of the eight cooking
  // fractions would show a total that doesn't match the per-recipe cards
  // a shopper already added up by hand. Only applies when no unit-family
  // conversion (oz→lb, cup→tbsp, ...) happened above, since a converted
  // unit invalidates the original 1/24ths total.
  const q =
    !isRealMetricConversion && ing.frac24 != null && displayUnit === ing.unit
      ? formatFraction24(ing.frac24)
      : roundQty(rawQ, isRealMetricConversion ? metricUnit : displayUnit, name);
  // Hide zero-quantity ingredients entirely
  if (!q || parseFrac(q) === 0) return "";
  const hint = pieceHint(name, q, isRealMetricConversion ? metricUnit : displayUnit);
  // Only treat this as a genuinely unit-less ingredient (candidate for the
  // "just show the mass-noun name, no quantity" fallback below) when there
  // was no unit to begin with — not when a real unit was suppressed above
  // for duplicating the ingredient name. The quantity is still meaningful
  // in the collision case ("1 leaf lettuce"), so it must still be shown.
  const countability = countabilityOf(ing);
  if (!unit && !unitCollidesWithName) {
    // A mass noun bought in pieces counts in its sub-unit, which carries the
    // plural: "3 cloves of garlic". The noun itself still never pluralizes.
    if (ing.subunit && q && !SUBUNIT_MODIFIER_RE.test(name)) {
      const wholeQ = String(Math.max(1, Math.ceil(parseFrac(rawQ))));
      return `${wholeQ} ${pluralizeUnit(ing.subunit, wholeQ)} of ${name}${
        hint ? " " + hint : ""
      }`.trim();
    }
    // A mass noun with no unit has nothing to count — "2 chicken" is not a
    // quantity anyone can act on, so show the name alone.
    if (countability === MASS) return name;
  }
  const displayName = pluralizeName(name, q, { countability, hasUnit: !!unit });
  return `${q}${unit ? ` ${unit}` : ""} ${displayName}${hint ? " " + hint : ""}`.trim();
}

export function fmtPantry(item, metric = false) {
  const ing = {
    ingredient_name: item.name,
    quantity: item.quantity,
    rawQuantity: item.quantity,
    unit: item.unit,
    quantity_metric: item.quantity_metric,
    unit_metric: item.unit_metric,
    // Staples are formatted by the same function, so they need the same
    // agreement data — without this every pantry line silently falls back
    // to the mass default.
    countability: item.countability,
    subunit: item.subunit,
  };
  return fmtIng(ing, metric);
}

export function scaleIng(ing, scale) {
  if (!scale || scale === 1) return ing;
  // parseFrac, not parseFloat: quantity is a string column, so "3/4" and
  // "1 1/2" are real stored values and parseFloat reads those as 3 and 1.
  const scaleNum = (raw) => {
    const n = parseFrac(raw);
    return isNaN(n) ? raw : String(n * scale);
  };
  return {
    ...ing,
    quantity: scaleNum(ing.quantity),
    rawQuantity: ing.rawQuantity != null ? scaleNum(ing.rawQuantity) : ing.rawQuantity,
    quantity_metric: ing.quantity_metric ? scaleNum(ing.quantity_metric) : ing.quantity_metric,
  };
}
